<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const fieldTable = [
  {
    field: 'Nama Supplier',
    type: 'Teks',
    required: true,
    desc: 'Nama perusahaan atau nama orang jika supplier perorangan.',
    example: '"CV Agro Nusantara" atau "Pak Budi Kopi"',
  },
  {
    field: 'Contact Person',
    type: 'Teks',
    required: false,
    desc: 'Nama orang yang bisa dihubungi. Berguna jika supplier adalah perusahaan besar.',
    example: '"Dian Pratiwi" atau "Mas Hendra"',
  },
  {
    field: 'Telepon',
    type: 'Angka/Teks',
    required: false,
    desc: 'Nomor HP atau telepon supplier. Mempermudah saat perlu konfirmasi pesanan.',
    example: '"08123456789" atau "021-55667788"',
  },
  {
    field: 'Email',
    type: 'Email',
    required: false,
    desc: 'Alamat email supplier untuk keperluan korespondensi resmi.',
    example: '"agro@example.com"',
  },
  {
    field: 'Alamat',
    type: 'Teks Panjang',
    required: false,
    desc: 'Alamat lengkap supplier. Berguna untuk pengiriman atau kunjungan langsung.',
    example: '"Jl. Raya Bogor No. 12, Depok, Jawa Barat"',
  },
  {
    field: 'Status Aktif',
    type: 'Toggle',
    required: false,
    desc: 'Supplier aktif bisa dipilih saat buat PO. Supplier nonaktif tidak muncul di daftar pilihan.',
    example: 'Aktif (default) / Nonaktif',
  },
]

const faq = [
  {
    q: 'Apakah saya harus isi semua kolom supplier?',
    a: 'Tidak. Hanya "Nama Supplier" yang wajib diisi. Kolom lain bersifat opsional. Namun makin lengkap data supplier, makin mudah Anda lacak jika ada masalah pesanan.',
  },
  {
    q: 'Bagaimana jika supplier saya adalah perorangan (bukan perusahaan)?',
    a: 'Tidak ada perbedaan. Isi "Nama Supplier" dengan nama orang tersebut, misalnya "Pak Hendra Kopi". Kosongkan saja field "Contact Person" jika orangnya sama.',
  },
  {
    q: 'Apakah supplier yang sudah punya Purchase Order bisa dihapus?',
    a: 'Tidak bisa dihapus permanen. Sistem akan otomatis menonaktifkan supplier tersebut (bukan menghapus) agar riwayat PO lama tetap terjaga. Supplier nonaktif tidak muncul saat Anda buat PO baru.',
  },
  {
    q: 'Saya punya supplier yang sama tapi untuk bahan berbeda. Perlu dibuat 2 data?',
    a: 'Tidak perlu. Satu supplier bisa Anda gunakan untuk berbagai jenis bahan baku. Cukup daftarkan sekali. Nanti saat buat PO, Anda bisa pilih bahan baku apa saja dari supplier yang sama.',
  },
  {
    q: 'Bagaimana cara menonaktifkan supplier sementara (misalnya sedang tidak operasional)?',
    a: 'Buka data supplier → klik Edit → matikan toggle "Supplier Aktif" → Simpan. Supplier tidak akan muncul di daftar pilihan saat buat PO. Aktifkan kembali kapan saja.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <div class="d-flex align-center gap-2 mb-3">
      <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
        User Guide
      </RouterLink>
      <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
      <RouterLink to="/userguide/pembelian" class="text-decoration-none text-medium-emphasis text-body-2">
        Pembelian
      </RouterLink>
      <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
      <span class="text-body-2 font-weight-medium">Supplier</span>
    </div>

    <!-- Header -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="primary" variant="tonal" size="64" rounded="lg" class="me-4">
            <VIcon icon="ri-store-2-line" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">Manajemen Supplier</h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Kelola data semua pemasok bahan baku toko Anda di satu tempat
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda mendaftarkan supplier (pemasok bahan baku) ke sistem PintuKasir.
          Supplier yang sudah terdaftar bisa dipilih saat membuat Purchase Order.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="primary" />
            Apa itu Supplier?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Supplier</strong> adalah orang atau perusahaan yang Anda beli bahan baku darinya.
              Misalnya coffee shop biasanya punya beberapa supplier berbeda:
            </p>

            <VTable class="mb-4">
              <thead>
                <tr>
                  <th>Contoh Supplier</th>
                  <th>Bahan yang Dipasok</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>CV Agro Nusantara</td>
                  <td>Biji kopi Arabika, Robusta</td>
                </tr>
                <tr>
                  <td>Pak Budi (perorangan)</td>
                  <td>Susu segar lokal, yogurt</td>
                </tr>
                <tr>
                  <td>Toko Grosir Makmur</td>
                  <td>Gula, sirup, creamer, kemasan</td>
                </tr>
              </tbody>
            </VTable>

            <p class="text-body-2 mb-0">
              Dengan mendaftarkan supplier di PintuKasir, Anda bisa membuat Purchase Order (surat pesanan)
              resmi, memantau riwayat pembelian per supplier, dan tidak perlu cari-cari kontak
              di WhatsApp setiap kali ingin pesan barang.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Tambah -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-add-circle-line" class="me-2" color="success" />
            Cara Menambah Supplier Baru
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-3">
                Buka menu <strong>Pembelian → Supplier</strong> dari sidebar kiri
              </li>
              <li class="mb-3">
                Klik tombol <strong>"+ Tambah Supplier"</strong> di pojok kanan atas halaman
              </li>
              <li class="mb-3">
                Isi form yang muncul:
                <VTable class="mt-3 mb-2">
                  <thead>
                    <tr>
                      <th style="min-width: 140px">Field</th>
                      <th style="min-width: 70px">Wajib?</th>
                      <th>Penjelasan & Contoh</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="f in fieldTable" :key="f.field">
                      <td><strong>{{ f.field }}</strong></td>
                      <td>
                        <VChip :color="f.required ? 'error' : 'default'" size="x-small" variant="tonal">
                          {{ f.required ? '✅ Wajib' : '❌ Opsional' }}
                        </VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        {{ f.desc }}
                        <br>
                        <span class="text-caption text-medium-emphasis">Contoh: {{ f.example }}</span>
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </li>
              <li class="mb-3">
                Klik tombol <strong>"Simpan"</strong>
              </li>
              <li>
                Supplier langsung bisa digunakan saat membuat Purchase Order ✅
              </li>
            </ol>

            <VAlert type="success" variant="tonal" class="text-body-2">
              <strong>Seberapa cepat?</strong>
              Menambah 1 supplier hanya butuh waktu sekitar <strong>1–2 menit</strong>.
              Isi nama dan nomor HP sudah cukup untuk memulai.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Edit & Nonaktifkan -->
    <VRow class="mb-4">
      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-edit-line" class="me-2" color="warning" />
            Edit Data Supplier
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">Klik ikon <strong>pensil (✏️)</strong> di baris supplier</li>
              <li class="mb-2">Ubah data yang perlu diperbarui</li>
              <li>Klik <strong>"Simpan Perubahan"</strong></li>
            </ol>
            <VAlert type="info" variant="tonal" density="compact" class="text-body-2">
              Perubahan data supplier <strong>tidak mempengaruhi</strong>
              Purchase Order yang sudah dibuat sebelumnya.
              Riwayat PO lama tetap terjaga.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-toggle-line" class="me-2" color="warning" />
            Nonaktifkan Supplier
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Gunakan ini jika supplier <strong>sementara tidak beroperasi</strong>
              atau Anda sudah tidak memesan dari mereka lagi.
            </p>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">Klik ikon pensil (Edit)</li>
              <li class="mb-2">Matikan toggle <strong>"Supplier Aktif"</strong></li>
              <li>Klik <strong>"Simpan Perubahan"</strong></li>
            </ol>
            <VAlert type="warning" variant="tonal" density="compact" class="text-body-2">
              Supplier nonaktif tidak muncul saat pilih supplier di form Purchase Order.
              Aktifkan kembali kapan saja dengan cara yang sama.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Hapus -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-delete-bin-line" class="me-2" color="error" />
            Menghapus Supplier
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Klik ikon <strong>tempat sampah (🗑️)</strong> di baris supplier → konfirmasi hapus.
            </p>
            <VAlert type="error" variant="tonal" class="text-body-2 mb-0">
              <strong>⚠️ Perlu diketahui:</strong>
              Jika supplier pernah digunakan di Purchase Order, sistem <strong>tidak akan menghapus datanya</strong>
              melainkan otomatis mengubah status menjadi <strong>Nonaktif</strong>.
              Ini untuk menjaga riwayat pembelian Anda tetap akurat.
              Jika belum pernah digunakan, supplier baru bisa dihapus permanen.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard color="primary" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact" bg-color="transparent">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Daftarkan semua supplier sebelum mulai buat PO.</strong>
                  Luangkan 10 menit di awal untuk input semua pemasok Anda.
                  Setelah itu, proses pembelian akan jauh lebih cepat.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Isi nomor HP supplier.</strong>
                  Walaupun opsional, nomor HP sangat membantu saat Anda perlu konfirmasi
                  pesanan atau tanya status pengiriman — semua ada di sistem, tidak perlu cari di kontak HP.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Nama supplier gunakan yang mudah dikenali tim.</strong>
                  Jika Anda punya karyawan yang juga buat PO, pastikan nama supplier
                  cukup jelas. Hindari nama singkat seperti "S1" atau "Pemasok Baru".
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mb-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Next Steps -->
    <VRow>
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Langkah Selanjutnya</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Purchase Order', sub: 'Buat PO setelah supplier terdaftar', icon: 'ri-file-list-3-line', color: 'warning', to: '/userguide/pembelian/purchase-order' },
              { title: 'Bahan Baku', sub: 'Pastikan bahan sudah ada sebelum buat PO', icon: 'ri-archive-line', color: 'info', to: '/userguide/produk-inventory/bahan-baku' },
              { title: 'Kembali ke Pembelian', sub: 'Lihat overview modul pembelian', icon: 'ri-truck-line', color: 'primary', to: '/userguide/pembelian' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
